import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import config from '../config';

const AuthContext = createContext(null);

// Configure axios for auth requests
export const authApi = axios.create({
  baseURL: config.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true
});

// Add request interceptor for auth token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor for debugging
authApi.interceptors.request.use(request => {
  console.log('API Request:', {
    method: request.method,
    url: request.url,
    data: request.data ? { ...request.data, password: '[REDACTED]' } : undefined
  });
  return request;
});

// Add response interceptor for debugging
authApi.interceptors.response.use(
  response => {
    console.log('API Response:', {
      status: response.status,
      data: response.data
    });
    return response;
  },
  error => {
    console.error('API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Add this effect to set up auth header when token changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          const userData = await fetchUser();
          setUser(userData);
        } catch (error) {
          console.error('Auth initialization error');
          localStorage.removeItem('token');
          delete authApi.defaults.headers.common['Authorization'];
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authApi.get('/api/auth/me');
      const userData = response.data.data;
      setUser(userData);
      return userData;
    } catch (error) {
      localStorage.removeItem('token');
      delete authApi.defaults.headers.common['Authorization'];
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await authApi.post('/api/auth/login', {
        email,
        password,
        rememberMe
      });

      const { token, user, pendingApproval, message } = response.data;
      
      // Always store in localStorage regardless of rememberMe
      localStorage.setItem('token', token);
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Set the user in state
      setUser(user);

      if (pendingApproval) {
        const pendingError = new Error(message);
        pendingError.pendingApproval = true;
        pendingError.user = user;
        throw pendingError;
      }

      return user;
    } catch (error) {
      if (error.pendingApproval) {
        throw error;
      }
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      console.log('Attempting registration with:', {
        ...userData,
        password: '[REDACTED]'
      });

      const response = await authApi.post('/api/auth/register', userData);
      console.log('Registration response:', response.data);

      if (response.data.success) {
        return response.data;
      }

      throw new Error(response.data.message || 'Registration failed');
    } catch (error) {
      console.error('Registration failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete authApi.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const requestPasswordReset = async (email) => {
    try {
      await authApi.post('/api/auth/forgot-password', { email });
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await authApi.post('/api/auth/reset-password', {
        token,
        password: newPassword
      });
    } catch (error) {
      throw error;
    }
  };

  const verifyResetToken = async (token) => {
    try {
      await authApi.get(`/api/auth/verify-reset-token/${token}`);
      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        login, 
        logout, 
        register, 
        isLoading,
        requestPasswordReset,
        resetPassword,
        verifyResetToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
